import {
  PermissionOutSchema,
  ProfileInSchema,
  SubscriptionPlanOutSchema,
} from "@/api/client";
import { PINIA_PERSIST_OPTIONS } from "@/helpers/constants";
import logger from "@/helpers/logger";
import { recommendationData, SignupData } from "@/helpers/types/global";
import { CurrentUserBusinessLevel } from "@/helpers/types/user";
import { defineStore } from "pinia";
import { useBusinessStore } from "./businesses.store";

import { handleError } from "@/api/handleError";
import { updateTeamMemberProfile } from "@/api/requests/teamMembers";
import { getFreeTrialData, getUserV2 } from "@/api/requests/users";
import { getUserVerification } from "@/api/requests/userVerification";
import { Profiles } from "@/helpers/types/business";

type Feedback = Record<
  string,
  {
    title: string;
    description: string;
    img: string;
    event: string;
  }
>;

type UsersStore = {
  user_role?: string | null;
  currentUser?: CurrentUserBusinessLevel | null;
  currentUserBusinessLevel?: CurrentUserBusinessLevel | null;
  registerUser: {
    id?: number | null;
    first_name?: string | null;
    last_name?: string | null;
    mobile?: string | null;
    personal_email?: string | null;
  };
  registerBusiness?: recommendationData | null;
  activeTab: number;
  permissions?: PermissionOutSchema | null;
  showFeedBackModal: boolean;
  NIN: string;
  hasFilledEngagement: boolean;
  savedSignupDetails?: SignupData | null;
  userFeedbackModal: boolean;
  feedback: Feedback;
  currentFeedback?:
    | (Feedback[keyof Feedback] & {
        data?: Record<string, string>;
      })
    | null;
  isFetchingUserDetail: boolean;
  recommended_plan?: SubscriptionPlanOutSchema | null;
  profiles?: Profiles | null;
  teamSize?: number | null;
};

export const useUsersStore = defineStore("users", {
  state: (): UsersStore => ({
    user_role: null,
    currentUser: null,
    currentUserBusinessLevel: null,
    registerUser: {
      id: null,
      first_name: "",
      last_name: "",
      mobile: "",
      personal_email: "",
    }, //user signup data
    registerBusiness: null,
    activeTab: 0, //for complete sign-up view
    permissions: null,
    showFeedBackModal: true,
    NIN: "",
    hasFilledEngagement: false,
    savedSignupDetails: null,
    userFeedbackModal: false,
    feedback: {
      callerGreeting: {
        title: "Rate your set up experience",
        description:
          "How would you rate the experience of setting up your first caller greeting. Your feedback would help us improve your experience.",
        img: "/assets/Images/feedback/greetings.png",
        event: "professional_greeting",
      },
    },
    currentFeedback: null,
    isFetchingUserDetail: false,
    recommended_plan: null,
    profiles: null,
    teamSize: null,
  }),
  getters: {
    getAgentRole: (state) => {
      return state.permissions;
    },
    userProfile: (state) => {
      return state?.profiles;
    },
    businesses: (state) => {
      return state?.profiles;
    },
    canAccessManagerView: (state) => {
      if (!state.currentUserBusinessLevel) {
        logger.warn({}, "User business not found");
        return false;
      }

      if (!state.user_role) {
        logger.warn({}, "User role not found - manager");
        return false;
      }

      return ["owner", "manager", "specialist"].includes(state.user_role);
    },
    canAccessAgentView: (state) => {
      if (!state.currentUserBusinessLevel) {
        logger.warn({}, "User business not found");
        return false;
      }

      if (!state.user_role) {
        logger.warn({}, "User role not found - agent");

        return false;
      }

      return ["agent"].includes(state.user_role);
    },
    isSpecialist: (state) => {
      if (!state.currentUserBusinessLevel) {
        logger.warn({}, "User business not found");
        return false;
      }

      if (!state.user_role) {
        logger.warn({}, "User role not found - specialist");

        return false;
      }

      return ["specialist"].includes(state.user_role);
    },
  },
  actions: {
    async updateUser({
      businessId,
      userId,
      profileInSchema,
    }: {
      businessId: number;
      userId: number;
      profileInSchema: ProfileInSchema;
    }) {
      const businessStore = useBusinessStore();

      await updateTeamMemberProfile({ businessId, profileInSchema, userId });

      await this.fetchUserDetails();

      await businessStore.fetchActiveUserProfile();
    },

    async fetchUserDetails() {
      const userResponse = await getUserV2();

      const currentUser = userResponse.data;

      this.setCurrentUser({
        ...this.currentUserBusinessLevel,
        ...currentUser,
      });
    },
    setFeedBack(feature: string) {
      this.currentFeedback = this.feedback[feature];
    },
    async fetchUserFreeTrialData() {
      const response = await getFreeTrialData();

      if (this.currentUserBusinessLevel) {
        this.currentUserBusinessLevel.free_trial_data = response.data;
      }
    },
    async getBusinessVerification() {
      // retrieve the list of businesses
      try {
        const response = await getUserVerification();

        const verificationDetails = response.data;

        if (this.currentUserBusinessLevel) {
          this.currentUserBusinessLevel.verification = verificationDetails;
        }
      } catch (error) {
        handleError({ error });
      }
    },
    setCurrentUser(user: CurrentUserBusinessLevel) {
      this.currentUserBusinessLevel = user;
      this.currentUser = user;
    },
  },
  persist: PINIA_PERSIST_OPTIONS,
});
